'use client';

import { useTranslations } from 'next-intl';
import { AnimatedNumber } from '@/components/ui/animated-number';
import { Icon } from '@/components/ui/icon';
import { Tick02Icon, UserMultipleIcon } from '@/components/ui/icon/glyphs';
import { type FormatMode, useFormattedCurrency } from '@/lib/currency';
import { getAnimatedD6Formatter } from '@/lib/format/animated-d6';
import { getFundingProgress } from '@/lib/format/funding-progress';
import { cn } from '@/lib/ui/utils';
import type { ProposalDisplayStatus } from './proposal-status-badge';

interface ProposalProgressBarProps {
  totalFunded: string;
  threshold: string | null;
  status: ProposalDisplayStatus;
  /** When set, renders funder count on the same row as the % funded label (to its right). */
  backerCount?: number;
  /**
   * When true, renders the bar as a halftone dot grid (green→lime gradient
   * masked into dots, dim-dot track behind). Status-based colours are not
   * applied under halftone — the surface always uses the funding-progress
   * identity. Used by the proposal detail page hero stake bar.
   */
  halftone?: boolean;
  /**
   * Caption format below the bar.
   * - `'percent-and-count'` (default): `[✓] N% funded · M funders` on the
   *   left, `funded / threshold` on the right. Used by the launchpad feed.
   * - `'amount-and-percent'`: single left-aligned line `funded / threshold (N%)`,
   *   used by the proposal detail page hero stake bar.
   */
  labelVariant?: 'percent-and-count' | 'amount-and-percent';
  /** Currency precision for the funded amount. Defaults to the existing compact treatment. */
  fundedFormatMode?: FormatMode;
  /** Currency precision for the threshold amount. Defaults to the existing compact treatment. */
  thresholdFormatMode?: FormatMode;
  /** Keep completed proposal summaries at 100%, even when they are overfunded. */
  capDisplayedPercentAt100?: boolean;
}

export function ProposalProgressBar({
  totalFunded,
  threshold,
  status,
  backerCount,
  halftone = false,
  labelVariant = 'percent-and-count',
  fundedFormatMode = 'compact',
  thresholdFormatMode = 'compact',
  capDisplayedPercentAt100 = false,
}: ProposalProgressBarProps): React.ReactNode {
  const t = useTranslations('launchpad.card');
  const tFeed = useTranslations('launchpad.feed');
  const { formatD6 } = useFormattedCurrency();
  const normalizedFunded = totalFunded.length > 0 ? totalFunded : '0';
  const normalizedThreshold = threshold === null || threshold.length === 0 ? '0' : threshold;
  const progress = getFundingProgress(normalizedFunded, normalizedThreshold);
  // `thresholdUnknown` means the backing-graduation cap hasn't been written
  // for this proposal yet (on-chain min_prize_pool_quote is 0 OR the
  // platform-config snapshot fallback wasn't seeded). Keep that state for
  // progress semantics while the numeric label uses a formatted zero.
  const thresholdUnknown = progress.displayPercent === null;
  const isMuted = status === 'expired' || status === 'rejected';
  const fundedDisplay = Number(BigInt(normalizedFunded));
  const thresholdDisplay = Number(BigInt(normalizedThreshold));
  const formatFunded = getAnimatedD6Formatter(formatD6, normalizedFunded, fundedFormatMode);
  const formatThreshold = getAnimatedD6Formatter(
    formatD6,
    normalizedThreshold,
    thresholdFormatMode,
  );
  const displayPercent =
    capDisplayedPercentAt100 && progress.displayPercent !== null
      ? Math.min(progress.displayPercent, 100)
      : progress.displayPercent;
  const displayedBackerCount =
    backerCount === undefined || !Number.isFinite(backerCount)
      ? undefined
      : Math.max(0, Math.trunc(backerCount));

  return (
    <div className="flex flex-col gap-1.5">
      <progress
        className="sr-only"
        aria-label={tFeed('fundingProgressLabel')}
        aria-valuetext={
          displayPercent === null
            ? undefined
            : t('fundingProgress', { percent: String(displayPercent) })
        }
        max={100}
        value={displayPercent === null ? undefined : Math.min(displayPercent, 100)}
      />
      <div
        className={cn(
          'relative w-full overflow-hidden rounded-full',
          halftone
            ? 'h-[var(--funding-progress-bar-height)] bg-funding-progress-track'
            : 'h-2 bg-foreground-primary/10',
          isMuted && 'opacity-40',
        )}
      >
        <div
          className={cn(
            'h-full transition-all',
            halftone
              ? cn(
                  'bg-funding-progress-halftone',
                  // Vertical column boundary between filled dots and track dots
                  // (rounded fill tips read as semi-circular "bulges" mid-bar).
                  progress.fillPercent >= 100 ? 'rounded-full' : 'rounded-l-full rounded-r-none',
                )
              : cn(
                  'rounded-full',
                  isMuted
                    ? 'bg-foreground-secondary/30'
                    : progress.isFunded
                      ? 'bg-feedback-success-accent'
                      : 'bg-feedback-info-accent',
                ),
          )}
          style={{ width: `${String(progress.fillPercent)}%` }}
        />
      </div>
      {labelVariant === 'amount-and-percent' ? (
        <div className="flex items-center gap-1 text-body tabular-nums">
          <span className="font-medium text-foreground-primary">
            <AnimatedNumber value={fundedDisplay} format={formatFunded} duration={400} />
            {' / '}
            {thresholdUnknown ? (
              <span className="text-foreground-tertiary">{formatThreshold(0)}</span>
            ) : (
              <AnimatedNumber value={thresholdDisplay} format={formatThreshold} duration={400} />
            )}
          </span>
          {!thresholdUnknown && (
            <span className="text-foreground-tertiary">({displayPercent}%)</span>
          )}
        </div>
      ) : (
        <div className="flex items-center justify-between gap-2 text-caption">
          <span
            className={cn(
              'flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 font-medium tabular-nums',
              isMuted
                ? 'text-foreground-secondary'
                : progress.isFunded
                  ? 'text-feedback-success-content'
                  : 'text-foreground-secondary',
            )}
          >
            {!thresholdUnknown && (
              <span className="inline-flex items-center gap-1">
                {progress.isFunded && <Icon icon={Tick02Icon} className="size-3" />}
                {t('fundingProgress', { percent: String(displayPercent) })}
              </span>
            )}
            {displayedBackerCount !== undefined && (
              <span className="inline-flex shrink-0 items-center gap-1 font-normal text-foreground-secondary">
                <Icon icon={UserMultipleIcon} className="size-3" />
                {t('backers', { count: displayedBackerCount })}
              </span>
            )}
          </span>
          <span className="text-foreground-secondary tabular-nums">
            <AnimatedNumber value={fundedDisplay} format={formatFunded} duration={400} />
            {' / '}
            {thresholdUnknown ? (
              <span className="text-foreground-tertiary">{formatThreshold(0)}</span>
            ) : (
              <AnimatedNumber value={thresholdDisplay} format={formatThreshold} duration={400} />
            )}
          </span>
        </div>
      )}
    </div>
  );
}
